import { ITrailerAttachment } from "../../model/TrailerAttachment";
import { IShipment } from "../../model/Shipment";
import { IPicture, IMediaFile } from "../../model/Image";
import type { IHosAvailability, IHosRuleset } from "hos-tools";
import { IFeatureFlagPayload } from "./actions";
import { IDeviceLink } from "../../model/DeviceLink";
import { FeatureFlagName } from "../../utils/featureFlagName";

export interface IIoxActionDictionary {
    [ dateTime: string ]: IoxActionTypes;
}

export interface ITempAssets {
    trailerAttachments?: ITrailerAttachment[];
    deviceLinks?: IDeviceLink[];
    shipments?: IShipment[];
}

export interface IImages {
    [ entityId: string ]: IPicture & IMediaFile;
}

/**
 * Defines the structure for cached addresses in the data store.
 * */
export interface ICachedAddress {
    formattedAddress?: string;
    formattedHosAddress?: string;
    logDate: string;
    country?: string;
    region?: string;
}

export type TELDJurisdiction = "ELD" | "CANELD" | "EWD";

export type IAlertsAvailability = {
    [Property in keyof IHosAvailability as string]?: {
        [key: number]: boolean;
    };
};

export interface IFeatureFlagStatus {
    name: string;
    isEnabled: boolean;
    value?: any;
}

export const enum ActiveEventType {
    VehicleRemoved = "vehicleRemoved",
    VehicleSelected = "vehicleSelected"
}

export interface IVehicleRemovedPayload {
    assignedVehicleId: string; // "NoDeviceId" when no new vehicle was assigned
    driverChangeDateTime?: string;
}

export interface IVehicleSelectedPayload {
    operationId?: string;
    deviceId: string;
    dateTime: string;
    userId: string;
    pendingUserIds?: string[];
}

export interface IActiveEvents {
    [ActiveEventType.VehicleRemoved]?: IVehicleRemovedPayload;
    [ActiveEventType.VehicleSelected]?: IVehicleSelectedPayload;
}

export interface IDataStore {
    boundaryLogDate: IIdDictionary<string>; // { UserID: ISO String }
    clockDriftMs: number;
    workday: IIdDictionary<WorkdayType>;    // { UserID: WorkdayType }
    tempAssets: IIdDictionary<ITempAssets>;
    activeContextId?: string;   // generate guid context ids?
    currentVehicle?: string;
    currentHardwareId?: string;
    currentTrailers?: string[];     // Remove? - always empty
    systemSettings?: {};
    logs?: string[];
    dtcNextRun: IIdDictionary<string>;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    logs_uploadq?: string[];
    lastUser?: string;
    lastServer?: string;
    nightMode: boolean;
    pipMode: boolean;
    lastDatabase?: string;
    gpsAccuracyAverage: number;
    groupSecurityId: ISecurityGroup[];
    driver?: string;    // remove?
    appVersion?: string;
    users?: {}[];   // turn into contexts?
    ioxLogActions: IIoxActionDictionary;
    images: IImages;
    acl: IIdDictionary<string[]>;
    addressCache: {[coordinates: string]: ICachedAddress};
    ruleSets: IHosRuleset[];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ELDJurisdiction: TELDJurisdiction;
    databaseVersion: string;
    sourceCodeVersion: string;
    clientVersion: string;
    serverFlags: ServerFlags;
    debugMode: boolean;
    inputHints: IIdDictionary<string[]>;
    inspectionPin: IIdDictionary<string>;
    alertsAvailability: IIdDictionary<IAlertsAvailability>;
    featureFlags: IFeatureFlagPayload;
    featureFlagOverrides: { [flagName in FeatureFlagName]?: boolean };
    originalFeatureFlags: IFeatureFlagPayload;
    lastLoginDate: {[userName: string]: string}; // { [userName]: Date ISO String }
    enableSyncDelay?: boolean;
    instanceId?: string;
    driving?: boolean;
    /** Motion state pushed by the DriveNative host while its movement service owns the lock screen; undefined otherwise. */
    nativeMotion?: NativeMotionState;
    handledSdcEndedPcLogIds: IIdDictionary<string>;
    lastVehicleLocation?: ICoordinate;
    activeEvents: IActiveEvents;
}

export type NativeMotionState = "moving" | "stopped" | "unknown";

export enum DataActionTypes {
    SET_BOUNDARY_LOG_DATE = "SET_BOUNDARY_LOG_DATE",
    CLEAR_BOUNDARY_LOG_DATE = "CLEAR_BOUNDARY_LOG_DATE",
    SET_NIGHTMODE = "SET_NIGHTMODE",
    SET_PIPMODE = "SET_PIPMODE",
    SET_CLOCK_DRIFT = "SET_CLOCK_DRIFT",
    SET_WORKDAY = "SET_WORKDAY",
    CLEAR_WORKDAY = "CLEAR_WORKDAY",
    SET_IOX_LOG_ACTION = "SET_IOX_LOG_ACTION",
    SET_IOX_HARDWAREID = "SET_IOX_HARDWAREID",
    SET_TEMP_ASSETS = "SET_TEMP_ASSETS",
    CLEAR_TEMP_ASSETS = "CLEAR_TEMP_ASSETS",
    SET_DTCNEXTRUN = "SET_DTCNEXTRUN",
    CLEAR_DTCNEXTRUN = "CLEAR_DTCNEXTRUN",
    SET_IMAGES = "SET_IMAGES",
    SET_ACL = "SET_ACL",
    CLEAR_ACL = "CLEAR_ACL",
    SET_GPS_ACCURACY_AVERAGE = "SET_GPS_ACCURACY_AVERAGE",
    CLEAR_ADDRESS_CACHE = "CLEAR_ADDRESS_CACHE",
    SET_ADDRESS_CACHE = "SET_ADDRESS_CACHE",
    SET_RULESETS_CACHE = "SET_RULESETS_CACHE",
    SET_ELD_JURISDICTION = "SET_ELD_JURISDICTION",
    SET_DATABASE_VERSION = "SET_DATABASE_VERSION",
    SET_SOURCE_CODE_VERSION = "SET_SOURCE_CODE_VERSION",
    SET_SERVER_FLAGS = "SET_SERVER_FLAGS",
    SET_DEBUGMODE = "SET_DEBUGMODE",
    SET_LAST_DATABASE = "SET_LAST_DATABASE",
    CLEAR_LAST_DATABASE = "CLEAR_LAST_DATABASE",
    SET_LAST_USER = "SET_LAST_USER",
    SET_LAST_SERVER = "SET_LAST_SERVER",
    CLEAR_LAST_SERVER = "CLEAR_LAST_SERVER",
    SET_INPUT_HINTS = "SET_INPUT_HINTS",
    CLEAR_INPUT_HINTS = "CLEAR_INPUT_HINTS",
    SET_GROUP_SECURITY_ID = "SET_GROUP_SECURITY_ID",
    CLEAR_GROUP_SECURITY_ID = "CLEAR_GROUP_SECURITY_ID",
    SET_INSPECTION_PIN = "SET_INSPECTION_PIN",
    CLEAR_INSPECTION_PIN = "CLEAR_INSPECTION_PIN",
    SET_ALERTS_AVAILABILITY = "SET_ALERTS_AVAILABILITY",
    CLEAR_ALERTS_AVAILABILITY = "CLEAR_ALERTS_AVAILABILITY",
    SET_FEATURE_FLAGS = "SET_FEATURE_FLAG_STATUS",
    SET_FEATURE_FLAG_OVERRIDE = "SET_FEATURE_FLAG_OVERRIDE",
    CLEAR_FEATURE_FLAG_OVERRIDE = "CLEAR_FEATURE_FLAG_OVERRIDE",
    CLEAR_ALL_FEATURE_FLAG_OVERRIDES = "CLEAR_ALL_FEATURE_FLAG_OVERRIDES",
    SET_LAST_LOGIN_DATE = "SET_LAST_LOGIN_DATE",
    CLEAR_LAST_LOGIN_DATE = "CLEAR_LAST_LOGIN_DATE",
    SET_ENABLE_SYNC_DELAY = "SET_ENABLE_SYNC_DELAY",
    SET_INSTANCE_ID = "SET_INSTANCE_ID",
    SET_DRIVING = "SET_DRIVING",
    SET_NATIVE_MOTION = "SET_NATIVE_MOTION",
    SET_HANDLED_SDC_ENDED_PC_LOG_ID = "SET_HANDLED_SDC_ENDED_PC_LOG_ID",
    SET_VEHICLE_LOCATION = "SET_VEHICLE_LOCATION",
    ADD_ACTIVE_EVENT = "ADD_ACTIVE_EVENT",
    REMOVE_ACTIVE_EVENT = "REMOVE_ACTIVE_EVENT",
    CLEAR_ALL_ACTIVE_EVENTS = "CLEAR_ALL_ACTIVE_EVENTS"
}

export enum WorkdayType {
    Started = 1,
    Stopped = 2,
    Initializing = 4
}

export enum IoxActionTypes {
    SET_AUTOOFF_TO_PC = "SET_AUTOOFF_TO_PC",
    SET_AUTOON_TO_D = "SET_AUTOON_TO_D"
}

export enum ServerFlags {
    None = 0, // Fedramp
    AllowThirdPartyLogging = 1
}
